'use client';

import {
  Showcase,
  ShowcaseStep,
} from '@wwe-portal/ui/components/showcase/showcase';
import {
  SUPERSTAR_DETAILS_STEP_IDS,
  useSuperstarTutorialStore,
} from '@wwe-portal/web/providers/stores/superstar-tutorial-store';
import { SUPERSTAR_DETAILS_TUTORIAL_SHOWCASE_ID } from '@wwe-portal/web/utils/superstar-details/constants';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useSuperstarDetailContext } from './context';
import { wait } from '@wwe-portal/ui/lib/timer';
import { TutorialStep } from '@wwe-portal/web/providers/stores/tutorial-store';
import { findShowcaseTarget } from '@wwe-portal/ui/components/showcase/primitives';
import {
  useGetCurrentUser,
  useGetUserInfo,
} from '@wwe-portal/web/providers/queries/user-info';
import {
  useIsAuthenticated,
  useIsAuthenticating,
} from '@wwe-portal/web/providers/stores/auth-store';
import { viewSuperstarTutorial } from '@wwe-portal/web/services/client';

const Highlight: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <span className="drop-shadow-card-low text-yellow800 font-medium">
      {children}
    </span>
  );
};

const StepContentLayout: FC<{
  order: number;
  total: number;
  children: ReactNode;
}> = ({ order, total, children }) => {
  return (
    <div>
      <div className="mb-2">
        <Highlight>{order}</Highlight>/<span>{total}</span>
      </div>
      {children}
    </div>
  );
};

const stepContentMap: Record<string, Partial<ShowcaseStep>> = {
  [SUPERSTAR_DETAILS_STEP_IDS.WELCOME]: {
    content: (
      <StepContentLayout order={1} total={5}>
        <div>
          Welcome to the new Set Base feature!{' '}
          <Highlight>
            The “Set Base” button lets you capture the current state
          </Highlight>{' '}
          so you can easily{' '}
          <Highlight>
            compare it with other states and see what&apos;s changed.
          </Highlight>{' '}
          Click Next to learn more in a quick overview!
        </div>
      </StepContentLayout>
    ),
  },
  [SUPERSTAR_DETAILS_STEP_IDS.SLIDE]: {
    content: (
      <StepContentLayout order={2} total={5}>
        <div>
          First,{' '}
          <Highlight>
            head over to the Superstar slider in the Superstar level
          </Highlight>{' '}
          section and <Highlight>adjust it to any value.</Highlight> You&apos;ll
          instantly <Highlight>see the difference</Highlight> between the two
          states.
        </div>
      </StepContentLayout>
    ),
  },
  [SUPERSTAR_DETAILS_STEP_IDS.SLIDING]: {
    content: null,
  },
  [SUPERSTAR_DETAILS_STEP_IDS.COMPARE]: {
    side: 'top',
    options: {
      offset: {
        y: -40,
        x: 'center',
      },
    },
    content: (
      <StepContentLayout order={3} total={5}>
        <div>
          Now you&apos;ll{' '}
          <Highlight>
            see the compared figures - showing the differences between this
            state with the previous state.
          </Highlight>{' '}
          Click Next to explore the next comparison!
        </div>
      </StepContentLayout>
    ),
  },
  [SUPERSTAR_DETAILS_STEP_IDS.REBASE]: {
    content: (
      <StepContentLayout order={4} total={5}>
        <div>
          To compare a new state with the current one,{' '}
          <Highlight>
            please click the Set Base button, then adjust the Superstar level
          </Highlight>{' '}
          to a different value. You’ll see the updated differences right away!
        </div>
      </StepContentLayout>
    ),
  },
  [SUPERSTAR_DETAILS_STEP_IDS.FINISH]: {
    finishText: 'DONE',
    content: (
      <StepContentLayout order={5} total={5}>
        <div>
          Similarly to Superstar Level, you{' '}
          <Highlight>
            can also compare changes with Star Colors, Select Stars, and
            Abilities.
          </Highlight>
        </div>
      </StepContentLayout>
    ),
  },
};

const STEP_ACTION_DELAY = 200;

export const SuperstarDetailsV2Tutorial = () => {
  const [scrollOffset, setScrollOffset] = useState<number>();
  const state = useSuperstarTutorialStore((state) => state);
  const superstarDetails = useSuperstarDetailContext((state) => state);
  const currentUser = useGetCurrentUser();
  const { isLoading: isUserLoading } = useGetUserInfo();
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const isTutorialTriggered = useRef<Record<string, boolean>>({});
  const steps = state.steps.map((step) => ({
    ...step,
    ...stepContentMap[step.id],
  }));
  const currentStep = steps.find((step) => step.id === state.currentStep?.id);

  const handleBeforeChangeStep = async (
    nextStep: TutorialStep,
    cancellation?: {
      isCancelled: boolean;
    }
  ) => {
    if (cancellation?.isCancelled) return false;

    if (nextStep.id === SUPERSTAR_DETAILS_STEP_IDS.WELCOME) {
      const setBaseEl = nextStep.targetId
        ? findShowcaseTarget(
            nextStep.targetId,
            SUPERSTAR_DETAILS_TUTORIAL_SHOWCASE_ID
          )
        : null;

      if (!setBaseEl) return false;

      await wait(STEP_ACTION_DELAY);
      if (cancellation?.isCancelled) return false;
      const scrollY = setBaseEl.getBoundingClientRect().y;
      setScrollOffset(scrollY);
      window.scrollTo({
        top: scrollY,
      });

      superstarDetails.changeLevel(1);
      return true;
    }

    if (
      nextStep.id === SUPERSTAR_DETAILS_STEP_IDS.SLIDING &&
      superstarDetails.currentStats.levelCap
    ) {
      state.goNext();
      await wait(STEP_ACTION_DELAY);
      const levelCap = Math.min(superstarDetails.currentStats.levelCap, 10);
      for (let i = 0; i < levelCap; i++) {
        superstarDetails.changeLevel(i);
        await wait(STEP_ACTION_DELAY);
      }
    }

    return true;
  };

  const handleGoNext = async () => {
    const nextStep = steps.find(
      (step) => step.id === state.currentStep?.nextId
    );
    if (nextStep) {
      const isHandled = await handleBeforeChangeStep(nextStep);
      if (!isHandled) return;
      state.goNext();
      return;
    }

    handleFinish();
  };

  const handleGoBack = async () => {
    const prevStep = steps.find(
      (step) => step.id === state.currentStep?.prevId
    );
    if (prevStep) {
      await handleBeforeChangeStep(prevStep);
    }
    state.goBack();
  };

  const handleFinish = () => {
    // unlock scroll offset
    setScrollOffset(undefined);
    superstarDetails.resetToDefault();
    state.finish();
  };

  // lock scroll offset
  useEffect(() => {
    const handleScroll = () => {
      if (scrollOffset == null) return;
      window.scrollTo({
        top: scrollOffset,
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollOffset]);

  const shouldShowTutorial = (() => {
    if (isAuthenticating || (isAuthenticated && isUserLoading)) return false;
    if (currentUser) return !currentUser.superstarTutorialStatus;
    return !state.isViewed;
  })();
  const userId = currentUser?.userId;

  useEffect(() => {
    const cancellation = {
      isCancelled: false,
    };
    window.history.scrollRestoration = 'manual';
    if (
      steps[0] &&
      shouldShowTutorial &&
      (!userId || !isTutorialTriggered.current[userId])
    ) {
      const firstStep = steps[0];
      const startTutorial = async () => {
        if (userId) {
          try {
            await viewSuperstarTutorial();
          } catch {
            // Don't block tutorial if API fails
          }
          if (cancellation.isCancelled) return;
          isTutorialTriggered.current[userId] = true;
        }
        await handleBeforeChangeStep(firstStep, cancellation);
        if (!cancellation.isCancelled) {
          state.start();
        }
      };
      startTutorial();
    }
    return () => {
      cancellation.isCancelled = true;
    };
  }, [shouldShowTutorial, userId]);

  if (!state.currentStep) return null;
  return (
    <Showcase
      steps={steps}
      currentStep={currentStep}
      isTransitioning={state.isTransitioning}
      options={{
        offset: {
          x: 'center',
          y: 50,
        },
      }}
      canSkip
      start={state.start}
      finish={handleFinish}
      close={handleFinish}
      goNext={handleGoNext}
      goBack={handleGoBack}
      showCaseId={SUPERSTAR_DETAILS_TUTORIAL_SHOWCASE_ID}
    />
  );
};
